/**
 * Central ⇄ Range (Floor / Central / Ceiling) scenario contract.
 *
 * The backend attaches an optional `scenarios` block to run responses
 * (`/projects/{id}/runs/latest`, `/loads/{id}/runs/latest`) and to
 * `/portfolio/overview`. Each posture band carries the
 * `Impacts.{Ops,CtG}.Total` endpoint triple plus the cradle-to-grave
 * midpoint category arrays (`Public_Health`, `Biodiversity`, `Economic`).
 * The TS shapes here mirror `totalview_schemas.api.sensitivity`
 * (`ScenariosBlock`, `ScenarioBand`, `BandTotals`, `ImpactTriple`).
 *
 * Postures are NOT raw low/mid/high — they roll BOTH dominant uncertainties
 * (the FRF gate AND the cross-border posture) into one envelope:
 *   Floor    → A3OFF: realized FRF, no cross-border credit (conservative)
 *   Central  → S0:    realized (measured FRF + δ cross-border) — the headline
 *   Ceiling  → S12:   FRF gate off, naive 100%-fossil displacement (upper bound)
 * `bands` additionally carries the A3ON intermediate so the whisker drill-down
 * can split the ceiling→central gap into its two measured discounts (FRF, then
 * un-realized cross-border). See `metricDecomposition`.
 *
 * The whole feature is additive: when `status !== 'complete'` (or the
 * block is absent), every consumer renders exactly as today — Central only.
 */

export type ScenarioStatus = "none" | "running" | "complete" | "stale";

/** A single (USD, DALYs, PDFs) headline triple. All nullable. */
export interface ImpactTriple {
  USD?: number | null;
  DALYs?: number | null;
  PDFs?: number | null;
}

/**
 * Ops + CtG endpoint totals for one band. Each analysis lens is a dict
 * keyed by (currently only) "Total", mirroring the engine summary_json
 * shape `Impacts.{Ops,CtG}.Total`.
 */
export interface BandTotals {
  Ops: Record<string, ImpactTriple>;
  CtG: Record<string, ImpactTriple>;
}

/** A single midpoint (ReCiPe sub-category) value inside a band. */
export interface MidpointEntry {
  Impact_Category: string;
  Total_Impact?: number;
  USD?: number;
}

/** One posture (floor / central / ceiling) — or the central value. */
export interface ScenarioBand {
  scenario_key: string;
  scenario_label?: string | null;
  Impacts: BandTotals;
  /** Cradle-to-grave midpoint arrays (headline basis). */
  Public_Health: MidpointEntry[];
  Biodiversity: MidpointEntry[];
  Economic: MidpointEntry[];
}

/** The optional block hung off run / overview responses. */
export interface ScenariosBlock {
  status: ScenarioStatus;
  floor?: ScenarioBand | null;
  central?: ScenarioBand | null;
  ceiling?: ScenarioBand | null;
  /**
   * Every swept scenario keyed by scenario_key (A3OFF / S0 / A3ON / S12).
   * floor/central/ceiling mirror three of these; A3ON is the extra
   * intermediate the whisker drill-down needs to split the FRF discount from
   * the cross-border discount. Absent on older responses.
   */
  bands?: Record<string, ScenarioBand> | null;
  /** Measured evidence behind the crediting ladder (FRF spread + neighbour δ). */
  realization?: RealizationEvidence | null;
  computed_at?: string | null;
}

/** One neighbouring grid's measured cross-border realization fraction δ. */
export interface NeighborDelta {
  area_code: string;
  eco_geography?: string | null;
  delta: number;
  delta_kind: string; // "measured" | "reasoned" | "reasoned-default"
  displaced_gwh_realized?: number | null;
  displaced_gwh_a3on?: number | null;
}

/** Measured FRF distribution + neighbour δ from the central run. */
export interface RealizationEvidence {
  frf_mean?: number | null;
  frf_median?: number | null;
  frf_p10?: number | null;
  frf_p90?: number | null;
  frf_applied?: boolean | null;
  marginal_method?: string | null;
  crossborder_posture?: string | null;
  neighbors: NeighborDelta[];
}

export type Posture = "floor" | "central" | "ceiling";
export type EndpointMetric = "dalys" | "pdfs" | "usd";
export type AnalysisLens = "ctg" | "ops";
export type MidpointSection = "Public_Health" | "Biodiversity" | "Economic";

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

/** A scenarios block with all three bands guaranteed present (non-null). */
export interface ReadyScenarios extends ScenariosBlock {
  floor: ScenarioBand;
  central: ScenarioBand;
  ceiling: ScenarioBand;
}

/** True only when the sweep is finished and the three bands are present. */
export function isRangeReady(
  s: ScenariosBlock | null | undefined,
): s is ReadyScenarios {
  return (
    !!s &&
    s.status === "complete" &&
    !!s.floor &&
    !!s.central &&
    !!s.ceiling
  );
}

function lensKey(lens: AnalysisLens): "Ops" | "CtG" {
  return lens === "ops" ? "Ops" : "CtG";
}

/** Pull one endpoint metric for one posture + lens. */
export function bandMetric(
  band: ScenarioBand | null | undefined,
  lens: AnalysisLens,
  metric: EndpointMetric,
): number | undefined {
  const t = band?.Impacts?.[lensKey(lens)]?.["Total"];
  if (!t) return undefined;
  const v =
    metric === "dalys" ? t.DALYs : metric === "pdfs" ? t.PDFs : t.USD;
  return v == null ? undefined : v;
}

export interface MetricRange {
  floor: number;
  central: number;
  ceiling: number;
}

/** Floor / central / ceiling for a single endpoint metric. */
export function metricRange(
  s: ScenariosBlock | null | undefined,
  lens: AnalysisLens,
  metric: EndpointMetric,
): MetricRange | null {
  if (!isRangeReady(s)) return null;
  const floor = bandMetric(s.floor, lens, metric);
  const central = bandMetric(s.central, lens, metric);
  const ceiling = bandMetric(s.ceiling, lens, metric);
  if (floor == null || central == null || ceiling == null) return null;
  return { floor, central, ceiling };
}

/**
 * Uniform floor/ceiling ratios against the central value for a metric.
 *
 * The displacement driver (generation) is fixed; every avoided-impact
 * quantity scales ~linearly with realized displacement, so a single
 * ratio pair maps a central value onto its floor/ceiling. Used where the
 * contract carries only fleet/endpoint bands and a per-row band is needed
 * (emissions substances, per-project portfolio rows).
 */
export function metricRatios(
  s: ScenariosBlock | null | undefined,
  lens: AnalysisLens,
  metric: EndpointMetric,
): { floor: number; ceiling: number } | null {
  const r = metricRange(s, lens, metric);
  if (!r || r.central === 0) return null;
  return { floor: r.floor / r.central, ceiling: r.ceiling / r.central };
}

/** Per-midpoint floor/central/ceiling for the breakdown bars (CtG basis). */
export interface MidpointRange {
  category: string;
  floor: number;
  central: number;
  ceiling: number;
}

function entryValue(e: MidpointEntry | undefined, useUsd: boolean): number {
  if (!e) return 0;
  return useUsd ? e.USD ?? 0 : e.Total_Impact ?? e.USD ?? 0;
}

/**
 * Align the three bands' midpoint arrays by category so each bar can draw
 * its floor→ceiling band. `section` selects Public_Health / Biodiversity /
 * Economic; Economic reads USD, the others read Total_Impact.
 */
export function midpointRanges(
  s: ScenariosBlock | null | undefined,
  section: MidpointSection,
): MidpointRange[] {
  if (!isRangeReady(s)) return [];
  const useUsd = section === "Economic";
  const c = s.central[section] ?? [];
  const f = s.floor[section] ?? [];
  const h = s.ceiling[section] ?? [];
  const byCat = (arr: MidpointEntry[]) => {
    const m = new Map<string, MidpointEntry>();
    for (const e of arr) m.set(e.Impact_Category, e);
    return m;
  };
  const fm = byCat(f);
  const hm = byCat(h);
  return c.map((ce) => ({
    category: ce.Impact_Category,
    floor: entryValue(fm.get(ce.Impact_Category), useUsd),
    central: entryValue(ce, useUsd),
    ceiling: entryValue(hm.get(ce.Impact_Category), useUsd),
  }));
}

/**
 * The economic externality is the MONETIZED sum across all three midpoint
 * sections — health USD (DALYs × VSL) + biodiversity USD ($/species·yr) +
 * resource USD — not just the `Economic` section alone. Combine per-category
 * USD across the three arrays, mirroring the classic report / comparison-chart
 * logic so the range breakdown reconciles to the economic endpoint total.
 */
export interface EconomicSections {
  Public_Health?: MidpointEntry[];
  Biodiversity?: MidpointEntry[];
  Economic?: MidpointEntry[];
}

export function combineEconomicUsd(
  sections: EconomicSections | null | undefined,
): MidpointEntry[] {
  const m = new Map<string, number>();
  for (const key of ["Public_Health", "Biodiversity", "Economic"] as const) {
    for (const e of sections?.[key] ?? []) {
      const usd = e.USD ?? 0;
      if (usd) m.set(e.Impact_Category, (m.get(e.Impact_Category) ?? 0) + usd);
    }
  }
  return [...m.entries()].map(([Impact_Category, USD]) => ({
    Impact_Category,
    USD,
  }));
}

/** Floor/central/ceiling for the COMBINED economic USD per category. */
export function economicUsdRanges(
  s: ScenariosBlock | null | undefined,
): MidpointRange[] {
  if (!isRangeReady(s)) return [];
  const fMap = new Map(
    combineEconomicUsd(s.floor).map((e) => [e.Impact_Category, e.USD ?? 0]),
  );
  const hMap = new Map(
    combineEconomicUsd(s.ceiling).map((e) => [e.Impact_Category, e.USD ?? 0]),
  );
  return combineEconomicUsd(s.central).map((ce) => ({
    category: ce.Impact_Category,
    floor: fMap.get(ce.Impact_Category) ?? 0,
    central: ce.USD ?? 0,
    ceiling: hMap.get(ce.Impact_Category) ?? 0,
  }));
}

/** Human posture labels for the whisker end captions. */
export const POSTURE_LABELS: Record<Posture, string> = {
  floor: "Floor",
  central: "Central",
  ceiling: "Ceiling",
};

// ---------------------------------------------------------------------------
// Whisker drill-down: the crediting chain (naive → −FRF → −cross-border →
// central → floor). Each adjacent Δ is one measured discount, answering the
// reviewer's "why only x%?" and "how do you know — FRF and cross-border?".
// ---------------------------------------------------------------------------

/** Scenario keys of the four decomposition nodes (mirror SWEEP_DECOMPOSITION). */
const DECOMP_KEYS = {
  naive: "S12", // FRF gate off — naive 100%-fossil displacement (ceiling)
  afterFrf: "A3ON", // FRF on, naive 1:1 cross-border
  central: "S0", // realized (FRF + δ cross-border)
  floor: "A3OFF", // conservative — no cross-border credit
} as const;

export interface MetricDecomposition {
  /** Raw signed endpoint values at each node. */
  naive: number;
  afterFrf: number;
  central: number;
  floor: number;
  /** Discounts (positive magnitudes) removed to reach the next node. */
  frfDiscount: number; // |naive − afterFrf|: the fossil unit only answers part
  crossborderDiscount: number; // |afterFrf − central|: only δ of the leak realized
  floorDiscount: number; // |central − floor|: the cross-border credit itself
  /** central / naive — the share of the naive claim we actually credit (0–1). */
  creditedFraction: number | null;
}

/**
 * Decompose one endpoint metric into its measured crediting chain. Needs all
 * four swept bands (S12 / A3ON / S0 / A3OFF) present in `s.bands`; returns null
 * otherwise so callers fall back to the plain whisker.
 */
export function metricDecomposition(
  s: ScenariosBlock | null | undefined,
  lens: AnalysisLens,
  metric: EndpointMetric,
): MetricDecomposition | null {
  const bands = s?.bands;
  if (!bands) return null;
  const naive = bandMetric(bands[DECOMP_KEYS.naive], lens, metric);
  const afterFrf = bandMetric(bands[DECOMP_KEYS.afterFrf], lens, metric);
  const central = bandMetric(bands[DECOMP_KEYS.central], lens, metric);
  const floor = bandMetric(bands[DECOMP_KEYS.floor], lens, metric);
  if (naive == null || afterFrf == null || central == null || floor == null) {
    return null;
  }
  return {
    naive,
    afterFrf,
    central,
    floor,
    frfDiscount: Math.abs(naive - afterFrf),
    crossborderDiscount: Math.abs(afterFrf - central),
    floorDiscount: Math.abs(central - floor),
    creditedFraction: naive !== 0 ? central / naive : null,
  };
}

/**
 * One project-level crediting ladder, expressed as percentages of the simple
 * "1-for-1" claim. The three discounts are physical (they gate displaced fossil
 * energy upstream of the impact factors), so the *fractions* are near-identical
 * across health / ecosystem / economic — we average the three endpoints to get
 * one honest, metric-agnostic ladder for the single consolidated explainer
 * (instead of arbitrarily picking one metric). Percentages sum to 100:
 *   credited + fossilNoResponse + crossBorder = 100.
 */
export interface CreditingLadder {
  /** central / simple — the share of the naive claim actually credited. */
  creditedPct: number;
  /** share cut because fossil doesn't fully back down (the FRF gate). */
  fossilNoResponsePct: number;
  /**
   * Share DECLINED vs a naive 1:1 cross-border credit (the un-realized leak,
   * A3ON→S0). Small when the neighbour δ is high. NOT "how much is
   * cross-border" — that is `crossBorderCreditPct` below.
   */
  crossBorderDeclinedPct: number;
  /**
   * Share of the credited effect that runs THROUGH neighbouring grids and which
   * we DO credit (the floor gap, S0→A3OFF). This is the honest "how much is
   * cross-border" number; dropping it entirely is the floor.
   */
  crossBorderCreditPct: number;
  /** domestic-only credited share (floor / simple). */
  floorPct: number;
  /** true only when essentially none of the effect crosses a grid boundary. */
  domestic: boolean;
}

export function creditingLadder(
  s: ScenariosBlock | null | undefined,
  lens: AnalysisLens,
): CreditingLadder | null {
  const metrics: EndpointMetric[] = ["dalys", "pdfs", "usd"];
  const credited: number[] = [];
  const fossil: number[] = [];
  const declined: number[] = [];
  const xbCredit: number[] = [];
  const floor: number[] = [];
  for (const m of metrics) {
    const d = metricDecomposition(s, lens, m);
    if (!d || d.naive === 0) continue;
    const n = Math.abs(d.naive);
    credited.push(Math.abs(d.central) / n);
    fossil.push(d.frfDiscount / n);
    declined.push(d.crossborderDiscount / n);
    xbCredit.push(d.floorDiscount / n);
    floor.push(Math.abs(d.floor) / n);
  }
  if (!credited.length) return null;
  const mean = (a: number[]) => a.reduce((x, y) => x + y, 0) / a.length;
  const crossBorderCreditPct = mean(xbCredit) * 100;
  return {
    creditedPct: mean(credited) * 100,
    fossilNoResponsePct: mean(fossil) * 100,
    crossBorderDeclinedPct: mean(declined) * 100,
    crossBorderCreditPct,
    floorPct: mean(floor) * 100,
    domestic: crossBorderCreditPct < 1,
  };
}
